import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Menu, LogOut, Search, Sun, Moon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS, NAV_SECTIONS, visibleNav } from "@/lib/permissions";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import NotificationsBell from "@/components/NotificationsBell";
import OnboardingModal, { useOnboardingGate, HelpButton } from "@/components/OnboardingModal";
import AIQuickAddFab from "@/components/AIQuickAddFab";
import { useAppearance } from "@/theme/ThemeProvider";
import DashboardTopBarCluster from "@/components/DashboardTopBarCluster";

/**
 * Phase 43 (2026-07-28) - one-click light/dark toggle in the top bar.
 * Respects the user's saved accent/font/contrast; only flips the theme
 * mode between "light" and "dark".
 */
function ThemeQuickToggle() {
  const { mode, setMode } = useAppearance();
  const isDark = mode === "dark" ||
    (mode === "system" && typeof window !== "undefined" &&
     window.matchMedia?.("(prefers-color-scheme: dark)")?.matches);
  const label = isDark ? "Switch to light mode" : "Switch to dark mode";
  return (
    <button
      type="button"
      onClick={() => setMode(isDark ? "light" : "dark")}
      className="p-2 rounded-full hover:bg-slate-100 text-slate-500 hover:text-slate-800 transition-colors"
      aria-label={label}
      title={label}
      data-testid="theme-quick-toggle"
    >
      {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
    </button>
  );
}

/* Phase 39 (2026-07-28) — Option 4 "Modern SaaS Cockpit" app shell.
 * White sidebar with pale-blue active pill + dot indicator.
 * Clean top bar with search, notifications, avatar chip.
 * All wiring preserved from prior AppLayout: visibleNav(can), NotificationsBell,
 * AIQuickAddFab, OnboardingModal, DropdownMenu profile. */

const SIDEBAR_SCROLL_KEY = "somturk:sidebar-scroll";
const BUILD_STAMP = process.env.REACT_APP_BUILD_STAMP || "2026-07-28 · Phase 39";

function initials(name = "") {
  return name
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

function SidebarContent({ items, user, onNavigate, onLogout, scrollKey = "desktop" }) {
  const grouped = NAV_SECTIONS.map((sec) => ({
    ...sec,
    items: items.filter((i) => i.section === sec.key),
  })).filter((g) => g.items.length > 0);

  const navRef = useRef(null);
  const storageKey = `${SIDEBAR_SCROLL_KEY}:${scrollKey}`;
  useEffect(() => {
    const el = navRef.current;
    if (!el) return;
    const saved = Number(sessionStorage.getItem(storageKey) || 0);
    if (saved > 0) el.scrollTop = saved;
    const onScroll = () => {
      sessionStorage.setItem(storageKey, String(el.scrollTop));
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [storageKey]);

  return (
    <div className="flex h-full flex-col bg-white text-slate-700" data-testid="sidebar-shell">
      {/* Brand header — clean white with SOMTURK wordmark */}
      <div className="px-5 py-5 border-b border-[#E4EBF4] shrink-0">
        <img
          src="/logo/somturk_wordmark_clean.png"
          alt="SOM-TURK PLC"
          className="h-8 w-auto select-none"
          draggable={false}
          data-testid="sidebar-logo"
        />
        <div className="mt-3 flex items-center gap-2">
          <span className="block h-[2px] w-6" style={{ background: "#E63946" }} />
          <span className="text-[10px] uppercase tracking-[0.24em] text-slate-500 font-medium">
            Finance Platform
          </span>
        </div>
      </div>

      {/* Scrollable nav */}
      <nav
        ref={navRef}
        className="flex-1 min-h-0 overflow-y-auto px-3 py-4 space-y-5 somturk-scroll"
        data-testid="sidebar-nav"
      >
        {grouped.map((sec) => (
          <div key={sec.key} className="space-y-1">
            <div
              className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-400"
              data-testid={`nav-section-${sec.key}`}
            >
              {sec.label}
            </div>
            {sec.items.map((it) => (
              <NavLink
                key={it.key}
                to={it.path}
                onClick={onNavigate}
                data-testid={`nav-${it.key}`}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 h-10 text-sm transition-colors relative ${
                    isActive
                      ? "bg-[#E7EEFB] text-[#1F4FA3] font-medium"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span
                      className={`inline-block h-1.5 w-1.5 rounded-full transition-opacity ${
                        isActive ? "bg-[#1F4FA3] opacity-100" : "opacity-0"
                      }`}
                    />
                    <span className="truncate">{it.label}</span>
                  </>
                )}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>

      {/* User card + build stamp */}
      <div className="shrink-0 border-t border-[#E4EBF4] px-3 py-3" data-testid="sidebar-user-card">
        <div className="flex items-center gap-3 rounded-lg border border-[#E4EBF4] px-3 py-2 bg-white">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-[#1F4FA3] text-white text-xs font-semibold">
              {initials(user?.full_name)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <p
              className="text-sm font-medium text-slate-900 leading-tight truncate"
              data-testid="sidebar-user-name"
            >
              {user?.full_name}
            </p>
            <p
              className="text-[12px] text-slate-500 leading-tight truncate"
              data-testid="sidebar-user-role"
            >
              {ROLE_LABELS[user?.role] || user?.role}
            </p>
          </div>
          <button
            onClick={onLogout}
            className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
            data-testid="sidebar-logout-button"
            aria-label="Sign out"
            title="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </div>
        <p className="mt-2 text-[10px] text-slate-400 text-center" data-testid="build-stamp">
          Build {BUILD_STAMP}
        </p>
      </div>
    </div>
  );
}

export default function AppLayout({ children, title }) {
  const { user, logout, can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { open: onbOpen, setOpen: setOnbOpen } = useOnboardingGate();

  const items = visibleNav(can);
  const currentItem = items.find((i) => i.path === location.pathname);
  const pageTitle = title || currentItem?.label || "Dashboard";

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const isDashboard = location.pathname === "/dashboard";
  const subtitle = isDashboard
    ? "Real-time view of company finances"
    : "SOM-TURK PLC Finance System";

  return (
    <div className="min-h-screen" style={{ backgroundColor: "var(--bg-page)" }}>
      {/* Desktop sidebar */}
      <aside
        className="hidden md:flex md:flex-col md:fixed md:inset-y-0 md:left-0 md:w-60 md:z-30 md:border-r md:border-[#E4EBF4]"
        data-testid="desktop-sidebar"
      >
        <SidebarContent items={items} user={user} onLogout={handleLogout} />
      </aside>

      {/* Mobile drawer */}
      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent
          side="left"
          className="p-0 w-64 border-none bg-white"
          data-testid="mobile-sidebar"
        >
          <SidebarContent
            items={items}
            user={user}
            scrollKey="mobile"
            onNavigate={() => setMobileOpen(false)}
            onLogout={async () => {
              setMobileOpen(false);
              await handleLogout();
            }}
          />
        </SheetContent>
      </Sheet>

      {/* Main column */}
      <div className="flex min-h-screen flex-col md:pl-60">
        {/* Top bar */}
        <header
          className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-[#E4EBF4] bg-white px-4 md:px-6"
          data-testid="top-bar"
        >
          <div className="flex items-center gap-3 min-w-0">
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden p-2 rounded-md hover:bg-slate-100"
              data-testid="mobile-menu-button"
              aria-label="Open menu"
            >
              <Menu className="h-5 w-5 text-slate-700" />
            </button>
            <div className="min-w-0">
              <h1
                className="text-[20px] md:text-[24px] font-semibold text-slate-900 truncate leading-tight"
                data-testid="page-title"
              >
                {pageTitle}
              </h1>
              <p className="hidden md:block text-sm text-slate-500">{subtitle}</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            {/* Phase 46 - Dashboard-only date pill + live clock + refresh */}
            <DashboardTopBarCluster />
            {/* Search */}
            <div
              className="hidden lg:flex items-center gap-2 rounded-lg bg-slate-50 px-3 h-9 w-[260px] border border-transparent hover:border-slate-200 transition-colors"
              data-testid="top-search"
            >
              <Search className="h-4 w-4 text-slate-400 shrink-0" />
              <input
                type="text"
                placeholder="Search transactions, invoices, entities…"
                className="flex-1 bg-transparent outline-none text-sm text-slate-700 placeholder:text-slate-400"
              />
              <span className="text-[11px] text-slate-400 border border-slate-200 rounded px-1.5 py-0.5">
                ⌘K
              </span>
            </div>

            <HelpButton />
            <ThemeQuickToggle />
            <NotificationsBell />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 rounded-full px-2 py-1 hover:bg-slate-100"
                  data-testid="user-menu-button"
                >
                  <div className="hidden sm:block text-right">
                    <p
                      className="text-sm font-medium text-slate-900 leading-tight"
                      data-testid="user-name"
                    >
                      {user?.full_name}
                    </p>
                    <p
                      className="text-[12px] text-slate-500 leading-tight"
                      data-testid="user-role"
                    >
                      {ROLE_LABELS[user?.role] || user?.role}
                    </p>
                  </div>
                  <Avatar className="h-9 w-9">
                    <AvatarFallback className="bg-[#1F4FA3] text-white text-sm font-semibold">
                      {initials(user?.full_name)}
                    </AvatarFallback>
                  </Avatar>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56" data-testid="user-menu">
                <DropdownMenuLabel>
                  <div className="font-medium">{user?.full_name}</div>
                  <div className="text-xs text-slate-500 font-normal">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <Link to="/dashboard" data-testid="user-menu-dashboard">
                    Dashboard
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/profile/security" data-testid="user-menu-security">
                    My Security
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="text-red-600 focus:text-red-600"
                  data-testid="user-menu-logout"
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        {/* Page content */}
        <main
          className="flex-1 p-4 md:p-8 page-fade relative"
          data-testid="main-content"
        >
          {children}
        </main>

        <AIQuickAddFab />
      </div>
      <OnboardingModal open={onbOpen} onClose={() => setOnbOpen(false)} />
    </div>
  );
}
